import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { SuggestionStatusBadge } from '@/features/proto/ProtoBadges'
import { computeSuggestionKpis } from '@/features/proto/suggestionsMetrics'
import { formatQtyWithUom } from '@/features/proto/warehouseOverviewMetrics'
import {
  listSuggestions,
  refreshSuggestions,
  dismissSuggestion,
  convertSuggestionToPr,
  sugDtoToUi,
  type PurchaseSuggestionDto,
  type PurchaseSuggestion,
  ProcurementApiClientError,
} from '@/services/procurement'

export function SuggestionsPage() {
  const navigate = useNavigate()
  const [dtos, setDtos] = useState<PurchaseSuggestionDto[]>([])
  const [skippedBaseUom, setSkippedBaseUom] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const list = await listSuggestions()
      setDtos(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách đề xuất')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  const suggestions: (PurchaseSuggestion & { code?: string; name?: string; uom: string })[] =
    useMemo(() => dtos.map(sugDtoToUi), [dtos])

  const kpis = useMemo(() => computeSuggestionKpis(suggestions), [suggestions])

  async function handleRefresh() {
    setRefreshing(true)
    setError(null)
    setActionSuccess(null)
    setSkippedBaseUom([])
    try {
      const res = await refreshSuggestions()
      setDtos(res.suggestions)
      setSkippedBaseUom(res.skipped_missing_base_uom)
      setActionSuccess('Đã tính lại đề xuất mua theo tồn kho thực tế.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi khi tính lại đề xuất')
    } finally {
      setRefreshing(false)
    }
  }

  async function handleConvert(id: string) {
    setError(null)
    setActionSuccess(null)
    try {
      await convertSuggestionToPr(id)
      setActionSuccess('Đã tạo PR từ đề xuất thành công.')
      await loadData()
      navigate('/purchasing/pr')
    } catch (err: any) {
      if (err instanceof ProcurementApiClientError) {
        if (err.code === 'BASE_UOM_MISSING') {
          setError(`Lỗi: ${err.message}. Không thể tạo PR vì vật tư thiếu đơn vị gốc.`)
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : 'Lỗi chuyển đề xuất thành PR')
      }
    }
  }

  async function handleDismiss(id: string) {
    setError(null)
    setActionSuccess(null)
    try {
      await dismissSuggestion(id)
      setActionSuccess('Đã bỏ qua đề xuất.')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi khi bỏ qua đề xuất')
    }
  }

  const columns: ProtoColumn<typeof suggestions[number]>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Tên vật tư',
        sortable: true,
        width: 240,
        cell: (s) => (
          <span className="font-semibold text-slate-800">
            {s.name || '—'}
          </span>
        ),
        sortValue: (s) => `${s.name || ''} ${s.code || ''}`,
      },
      {
        id: 'policy',
        header: 'Chính sách',
        cell: (s) => s.policy,
        sortValue: (s) => s.policy,
      },
      {
        id: 'avail',
        header: 'Available',
        align: 'right',
        cell: (s) => formatQtyWithUom(s.available, s.uom),
        sortValue: (s) => s.available,
      },
      {
        id: 'inc',
        header: 'Incoming',
        align: 'right',
        cell: (s) => formatQtyWithUom(s.incoming, s.uom),
        sortValue: (s) => s.incoming,
      },
      {
        id: 'proj',
        header: 'Projected',
        align: 'right',
        cell: (s) => (
          <span className="font-semibold text-emerald-800">
            {formatQtyWithUom(s.projected, s.uom)}
          </span>
        ),
        sortValue: (s) => s.projected,
      },
      {
        id: 'qty',
        header: 'Đề xuất mua',
        align: 'right',
        cell: (s) => formatQtyWithUom(s.suggestedQty, s.uom),
        sortValue: (s) => s.suggestedQty,
      },
      {
        id: 'need',
        header: 'Ngày cần',
        cell: (s) => s.needByDate || '—',
        sortValue: (s) => s.needByDate || '',
      },
      {
        id: 'reason',
        header: 'Lý do',
        cell: (s) => (
          <span className="text-xs text-[var(--color-text-muted)]">{s.reason}</span>
        ),
        sortValue: (s) => s.reason,
      },
      {
        id: 'status',
        header: 'TT',
        cell: (s) => <SuggestionStatusBadge status={s.status} />,
        sortValue: (s) => s.status,
      },
      {
        id: 'act',
        header: '',
        cell: (s) =>
          s.status === 'OPEN' ? (
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                onClick={() => void handleConvert(s.id)}
              >
                → PR
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void handleDismiss(s.id)}
              >
                Bỏ
              </Button>
            </div>
          ) : (
            <span className="text-xs text-slate-400">{s.status}</span>
          ),
        sortValue: () => '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Đề xuất mua' },
        ]}
      />
      <PageHeader
        title="Đề xuất mua"
        subtitle="Postgres SoT · Available < Min nhưng Incoming đủ → không đề xuất dư. Safety Stock tính vào ngưỡng."
        actions={
          <Button
            variant="secondary"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
          >
            {refreshing ? 'Đang tính...' : 'Tính lại'}
          </Button>
        }
      />

      {error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {actionSuccess}
        </div>
      ) : null}

      {skippedBaseUom.length > 0 ? (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold">
            Có {skippedBaseUom.length} vật tư bị bỏ qua không tạo gợi ý vì thiếu đơn vị gốc (uom_default):
          </div>
          <div className="mt-1 font-mono text-xs">
            {skippedBaseUom.join(', ')}
          </div>
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tổng" value={kpis.total} />
        <KpiCard label="Mở" value={kpis.open} tone={kpis.open ? 'warning' : 'default'} />
        <KpiCard label="Đã → PR" value={kpis.converted} />
        <KpiCard label="Đã bỏ" value={kpis.dismissed} />
      </div>

      <PageToolbar
        left={
          <span className="text-xs text-[var(--color-text-muted)]">
            {kpis.open} đề xuất mở · số lượng kèm ĐVT vật tư
          </span>
        }
      />

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-400">Đang tải đề xuất...</div>
      ) : suggestions.length === 0 ? (
        <EmptyState
          title="Chưa có đề xuất mua"
          description="Bấm 'Tính lại' để chạy chính sách replenish trên tồn kho Postgres."
          action={
            <Button
              variant="secondary"
              onClick={() => void handleRefresh()}
              disabled={refreshing}
            >
              {refreshing ? 'Đang tính...' : 'Tính lại'}
            </Button>
          }
        />
      ) : (
        <ProtoExcelTable
          showRowNumbers
          columns={columns}
          rows={suggestions}
          rowKey={(r) => r.id}
          exportName="de-xuat-mua"
          stickyLeftColumnIds={['name']}
          stickyRightColumnIds={['act']}
          emptyMessage="Không có đề xuất."
        />
      )}
    </div>
  )
}
